import * as React from "react";
import { cn } from "../../lib/cn";

/**
 * Composer.Bar — bottom action row.
 *
 * Two slots (left / right). Typically:
 *   - left:  `<Composer.AttachButton />` and any extra text-buttons
 *   - right: `<Composer.SendButton />` (single accent CTA)
 *
 * Use as a structural wrapper — no opinions about which children go where
 * (children are rendered in DOM order with `justify-between`). For finer
 * control, render `Bar` with a `left` + `right` prop pair instead.
 */
export interface ComposerBarProps {
  /** Left-slot content (typically attach overflow + capability buttons). */
  left?: React.ReactNode;
  /** Right-slot content (typically the send button). */
  right?: React.ReactNode;
  /** Inline children — used when `left`/`right` aren't supplied. */
  children?: React.ReactNode;
  className?: string;
}

export const ComposerBar = React.forwardRef<HTMLDivElement, ComposerBarProps>(
  ({ left, right, children, className }, ref) => {
    const hasSlots = left !== undefined || right !== undefined;
    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center justify-between gap-1 px-2 pt-0.5 pb-1.5",
          className
        )}
      >
        {hasSlots ? (
          <>
            {/* Left slot: wraps to a second row on narrow viewports so the
                ToolsMenu / SearchPower / Voice / FolderPicker stack never
                collides with the right-slot Send button (May 23, 2026 audit). */}
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-1 gap-y-1">
              {left}
            </div>
            <div className="flex shrink-0 items-center gap-2.5">{right}</div>
          </>
        ) : (
          children
        )}
      </div>
    );
  }
);
ComposerBar.displayName = "Composer.Bar";
